import { useCallback, useEffect, useState } from 'react';
import { useMask } from '@react-input/mask';
import auth0 from 'auth0-js';

import { getConfig } from '../../config';
import { sendOTP, setLocal } from '../../utils/api';

import './Phone.css';

const config = getConfig();
const webAuth = new auth0.WebAuth({
  domain: config.domain,
  clientID: config.clientId,
  responseType: 'token id_token',
  scope: 'openid profile email',
  redirectUri: window.location.origin,
  audience: config.audience
});

function Phone({ handleError, handlePhone }) {
  const inputRefOTP = useMask({
    mask: '_ _ _ _ _ _',
    replacement: { _: /\d/ },
    showMask: true
  });
  const inputRefPhone = useMask({
    mask: '+1 (___) ___-____',
    replacement: { _: /\d/ }
  });

  const [isOTP, setIsOTP] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [OTP, setOTP] = useState('');
  const [phone, setPhone] = useState('');

  const postOTP = useCallback(async (otp) => {
    otp = otp.replace(/ /gi, '');

    if (!/^[0-9]{6}$/.test(otp)) {
      handleError({ message: 'Invalid one-time passcode' });
      return;
    }

    try {
      setIsSending(true);

      webAuth.passwordlessLogin({
        connection: 'sms',
        phoneNumber: phone,
        verificationCode: otp
      }, function (e, result) {
        setIsSending(false);

        if (e) {
          handleError({ message: e.error_description });
        } else if (!result?.idToken) {
          handleError({ message: 'Could not authenticate' });
        } else {
          setLocal('token', result.idToken);
        }
      });
    } catch (e) {
      setIsSending(false);
      handleError(e);
      handlePhone(false);
    }
  }, [handleError, handlePhone, phone]);
  
  const handleOTPInput = useCallback((e) => {
    setOTP(e.target.value);
  }, []);

  const handleOTPKey = useCallback(async (e) => {
    const otp = e.target.value;

    if (e.keyCode === 13 && otp.length) {
      await postOTP(otp);
    }
  }, [postOTP]);

  const postPhone = useCallback(async (phone) => {
    phone = phone.replace(/[ ()-]/gi, '');
        
    if (!/^\+1[0-9]{10}$/.test(phone)) {
      handleError({ message: 'Invalid phone number' });
      return;
    }

    try {
      setPhone('');
      setIsSending(true);

      const response = await sendOTP(phone);

      if (response !== null) {
        setIsSending(false);
        setOTP('');
        setIsOTP(true);
        setPhone(phone);
      }
    } catch (e) {
      setPhone(phone);
      setIsSending(false);
      handleError(e);
    }
  }, [handleError]);

  const handlePhoneInput = useCallback((e) => {
    setPhone(e.target.value);
  }, []);

  const handlePhoneKey = useCallback(async (e) => {
    const phone = e.target.value;

    if (e.keyCode === 13 && phone.length) {
      await postPhone(phone);
    }
  }, [postPhone]);

  useEffect(() => {
    if (inputRefOTP?.current) {
      inputRefOTP.current.focus();
    }
    if (inputRefPhone?.current) {
      inputRefPhone.current.focus();
    }
  });

  return (
    <div id="phone-container" className="popup-overlay">
      <div className="popup">
        <div className="popup-title">Sign in with a One-time Code</div>
        {isOTP ? (
          <>
            <p>A one-time passcode has been sent to the phone number you provided.</p>
            <p>Please enter the one-time passcode below. If you did not receive one, you may <a href="#" onClick={() => postPhone(phone)}>request another one</a>.</p>
            <div className="text-input">
              {/* <img className="icon-chat" src={process.env.PUBLIC_URL + '/assets/icons/guide.svg'} alt="Chat" /> */}
              <input ref={inputRefOTP} type="input" value={OTP} name="otp" placeholder={!!isSending ? 'Give me a sec...' : 'One-Time Passcode'} autoComplete="off" disabled={!!isSending} onChange={(e) => handleOTPInput(e)} onKeyDown={(e) => handleOTPKey(e)} />
              <img className="icon-submit" src={process.env.PUBLIC_URL + '/assets/icons/submit.svg'} alt="Submit" onClick={() => {postOTP(OTP)}} />
            </div>
            <div className="actions">
              <div className="action action-secondary" onClick={() => handlePhone(false)}>Cancel</div>
              <div className="action action-primary" onClick={() => postOTP(OTP)}>Verify Passcode</div>
            </div>
          </>
        ) : (
          <>
            <p>Please enter your 10-digit phone number.</p>
            <p>Your phone number will only be used to send a one-time passcode for login verification. We do not use your phone number for any other purpose, and no additional information is sent via SMS.</p>
            <div className="text-input">
              {/* <img className="icon-chat" src={process.env.PUBLIC_URL + '/assets/icons/guide.svg'} alt="Chat" /> */}
              <input ref={inputRefPhone} type="input" value={phone} name="phone" placeholder={!!isSending ? 'Give me a sec...' : 'Phone Number'} autoComplete="off" disabled={!!isSending} onChange={(e) => handlePhoneInput(e)} onKeyDown={(e) => handlePhoneKey(e)} />
              <img className="icon-submit" src={process.env.PUBLIC_URL + '/assets/icons/submit.svg'} alt="Submit" onClick={() => {postPhone(phone)}} />
            </div>
            <div className="actions">
              <div className="action action-secondary" onClick={() => handlePhone(false)}>Cancel</div>
              <div className="action action-primary" onClick={() => postPhone(phone)}>Send Passcode</div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Phone;